import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Phase 10B 2050 PageHeader
 * - Optional uppercase eyebrow + breadcrumb (live-border accent on the active crumb)
 * - 34px display title, tight tracking
 * - Right-side actions slot
 */
const PageHeader = React.forwardRef(
  (
    {
      className,
      eyebrow,         // string OR React node — small uppercase label
      breadcrumb,      // [{ label, onClick? }, …] — last item assumed current
      title,
      description,
      actions,
      children,
      ...props
    },
    ref
  ) => {
    const crumbs = breadcrumb && breadcrumb.length > 0 ? breadcrumb : null;
    return (
      <header
        ref={ref}
        className={cn(
          "flex items-end justify-between gap-4 pb-5 border-b border-ink-200/60 dark:border-ink-800/60",
          className
        )}
        {...props}
      >
        <div className="min-w-0 space-y-1.5">
          {(eyebrow || crumbs) && (
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ink-500">
              {eyebrow && <span className="live-border pb-0.5">{eyebrow}</span>}
              {eyebrow && crumbs && <span className="text-ink-300">/</span>}
              {crumbs &&
                crumbs.map((c, i) => {
                  const last = i === crumbs.length - 1;
                  return (
                    <React.Fragment key={`${c.label}-${i}`}>
                      <button
                        type="button"
                        onClick={c.onClick}
                        disabled={!c.onClick || last}
                        className={cn(
                          "transition-colors",
                          last
                            ? "text-ink-700 dark:text-ink-200 normal-case tracking-normal text-xs cursor-default"
                            : "hover:text-ink-900 dark:hover:text-ink-50"
                        )}
                      >
                        {c.label}
                      </button>
                      {i < crumbs.length - 1 && <span className="text-ink-300">/</span>}
                    </React.Fragment>
                  );
                })}
            </div>
          )}
          {title && (
            <h1 className="font-display text-[28px] sm:text-[34px] leading-[1.05] font-semibold tracking-tight">
              {title}
            </h1>
          )}
          {description && (
            <p className="text-sm text-ink-500">{description}</p>
          )}
          {children}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>
        )}
      </header>
    );
  }
);
PageHeader.displayName = "PageHeader";

export { PageHeader };
